import axios from "axios";
import webTokenStorer, { selectedSocietyStorer } from "../utils/webStorer";
import { emitAccessDenied, isPermissionMessage } from '../utils/accessDenied';
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_NODE_MOBILE_BASE_URL,
});

// Matches backend messages like "Invalid or expired token" / "Unauthorized"
const isTokenError = (message) =>
  typeof message === "string" &&
  /(invalid|expired)[^a-z]*token|token[^a-z]*(invalid|expired)|unauthori[sz]ed/i.test(message);

// True when the user is already on a public auth page (avoids redirect loops)
const isAuthPage = () => {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return p === "/" || /\/(login|sign-up|forgot-password)/.test(p);
};

// Session is invalid/expired → clear stored tokens and bounce to the login page
const redirectToLogin = () => {
  if (typeof window === "undefined" || isAuthPage()) return;
  webTokenStorer.removeToken();
  const target = window.location.pathname.startsWith("/society-controller")
    ? "/society-controller/login"
    : "/society-portal/login";
  window.location.replace(target);
};


//- Add a request interceptor
axiosInstance.interceptors.request.use(
  function (config) {
    console.log("config", config.data);
    const isController = typeof window !== 'undefined' && window.location.pathname.startsWith('/society-controller');
    const userData = isController
      ? (webTokenStorer.getToken2() || webTokenStorer.getToken())
      : (webTokenStorer.getToken() || webTokenStorer.getToken2());

    if (userData?.access_token) {
      config.headers['Authorization'] = `Bearer ${userData.access_token}`;
    }

    // Scope every request to the active society. The backend uses this header
    // to resolve society context and to record the user's "last society".
    // Endpoints that carry the society id in the URL (e.g. /api/societies/:id)
    // ignore it, so sending it unconditionally is safe. Individual requests can
    // still override X-Society-Id via their own config.headers.
    if (config.headers['X-Society-Id'] == null) {
      const activeSocietyId = selectedSocietyStorer.getSocietyId();
      if (activeSocietyId != null) {
        config.headers['X-Society-Id'] = activeSocietyId;
      }
    }

    // Role switch: a member can hold several roles in one society (Resident and
    // Secretary, say). The selected membership's role tells the backend which
    // one this request acts as; it only picks among the user's own roles.
    if (config.headers['X-Role-Id'] == null) {
      const activeRoleId = selectedSocietyStorer.getRoleId();
      if (activeRoleId != null) {
        config.headers['X-Role-Id'] = activeRoleId;
      }
    }

    return config;
  },

  function (error) {
    //- Do something with request error
    return Promise.reject(error);
  }
);

//- Add a response  interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    console.log("interceptorresponse ", response);

    // Some endpoints return HTTP 200 with { success:false, message:"Invalid or expired token" }
    const body = response?.data;
    if (body && body.success === false && isTokenError(body.message)) {
      redirectToLogin();
    }

    //- return response;
    return body;
  },
  function (error) {
    // Surface the backend's user-facing message on error.message so existing
    // sagas that read `e?.message` get the real message instead of the
    // generic "Request failed with status code XXX".
    const serverMessage = error?.response?.data?.message;
    if (serverMessage) {
      error.message = serverMessage;
    }

    // Expired/invalid session (401, or token error message) → redirect to login
    if (error?.response?.status === 401 || isTokenError(serverMessage)) {
      redirectToLogin();
    }

    // Refused on permissions (403) → the route shows the Access Denied page
    // instead of a toast that disappears and leaves an empty screen.
    if (error?.response?.status === 403 || isPermissionMessage(serverMessage)) {
      error.isAccessDenied = true;
      emitAccessDenied({
        url: error?.config?.url || '',
        method: error?.config?.method || '',
        message: serverMessage || 'You do not have permission for this action.'
      });
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;